import React from "react";
import {Box, Text, Badge, Progress} from "@mantine/core";
import {UnifiedFileEntry} from "../hooks/useUnifiedFiles";
import {useUploadStatusStore} from "@/protocols/persistence/useUploadStatusStore";
import {secondsUntilCopy, PendingUpload} from "@/protocols/persistence/uploadTypes";

interface FileStatusCellProps {
    entry: UnifiedFileEntry;
}

export const FileStatusCell: React.FC<FileStatusCellProps> = ({entry}) => {
    // Look up the matching Rust queue row so the 'queued' badge can
    // show the watcher's age-gate countdown (matches the table cell
    // and the PendingUploadTable 'Waiting Xs' badge).
    const pendingRow: PendingUpload | undefined = useUploadStatusStore((s) =>
        entry.pendingUploadId !== undefined
            ? s.pendingUploads.find((r) => r.id === entry.pendingUploadId)
            : undefined,
    );

    if (entry.kind === 'queued') {
        if (pendingRow && pendingRow.state === 'found') {
            const remaining = secondsUntilCopy(pendingRow);
            if (remaining > 0) {
                return (
                    <Badge size="sm" color="blue" data-testid="upload-waiting">
                        Waiting - {String(remaining).padStart(2, '0')}s
                    </Badge>
                );
            }
        }
        return <Badge size="sm" color="blue">Queued</Badge>;
    }
    if (entry.kind === 'uploading') {
        const percentage = Math.round((entry.progress ?? 0) * 100);
        return (
            <Box>
                <Box style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                    <Badge size="sm" color="green">Uploading</Badge>
                    <Text size="xs" style={{fontFamily: "var(--mantine-font-family-fixed)"}}>
                        {percentage}%
                    </Text>
                </Box>
                <Progress value={percentage} color="green" size="xs" mt={4} />
            </Box>
        );
    }
    if (entry.kind === 'failed') {
        return (
            <Badge size="sm" color="red" title={entry.error ?? undefined} data-testid="upload-failed">
                Failed
            </Badge>
        );
    }
    if (entry.kind === 'pending_visibility') {
        return <Badge size="sm" color="teal" variant="light">Just uploaded</Badge>;
    }
    return <Badge size="sm" color="gray" variant="filled">Done</Badge>;
};